import type { ReactNode } from 'react';

import { idForSlug, slugFor } from '../../shared/article-slugs.js';
import { articlePath, articleSlugFromPath } from '../../shared/article-types.js';
import { localePath, type Locale } from '../../shared/locales.js';
import { LanguageMenu } from '../components/LanguageMenu.js';
import { Logo } from '../components/Logo.js';
import { BRAND } from '../config/brand.js';
import { useHref, useLocale, useStrings } from '../i18n.js';

/**
 * Public site chrome.
 *
 * A small wordmark, three links, a language menu, and no account button, because there
 * are no accounts. The same chrome never appears on a private room: that page gets its
 * own, quieter header.
 *
 * Every link is written as an English route and rewritten into the active language, so a
 * visitor reading in Portuguese stays in Portuguese as they move around the site.
 */

interface LayoutProps {
  path: string;
  children: ReactNode;
}

const NAV = [
  { href: '/how-it-works', key: 'navHowItWorks' as const },
  { href: '/guides', key: 'navGuides' as const },
  { href: '/blog', key: 'navBlog' as const },
  { href: '/faq', key: 'navFaq' as const },
  { href: '/privacy', key: 'navPrivacy' as const },
];

/**
 * Where the language menu points on an article page.
 *
 * Articles have a different slug in every language, so the ordinary "same path, other
 * prefix" rule would send a French reader to `/fr/blog/<english-slug>`, which does not
 * exist. Returns undefined for every other page, where that rule is correct.
 *
 * A language without this article gets the article index instead of a dead link. The slug
 * map is always bundled, so the server and the browser compute the same answer.
 */
function switcherHref(path: string, current: Locale): ((locale: Locale) => string) | undefined {
  const slug = articleSlugFromPath(path);
  if (slug === null) return undefined;

  const id = idForSlug(slug, current);
  if (id === undefined) return undefined;

  return (locale: Locale) => {
    const translated = slugFor(id, locale);
    return localePath(locale, translated === undefined ? '/blog' : articlePath(translated));
  };
}

export function Layout({ path, children }: LayoutProps) {
  const s = useStrings();
  const locale = useLocale();
  const href = useHref();

  return (
    <div className="page">
      <a className="skip-link" href="#main">
        {s.common.skipToContent}
      </a>

      <header className="site-header">
        <div className="site-header__inner">
          <a className="site-brand" href={href('/')}>
            <Logo size={30} className="site-brand__mark" />
            <span>{BRAND.siteName}</span>
          </a>
          <nav className="site-nav" aria-label={s.common.navLandmarkMain}>
            {NAV.map((item) => (
              <a
                key={item.href}
                className="site-nav__link"
                href={href(item.href)}
                {...(path === item.href ? { 'aria-current': 'page' as const } : {})}
              >
                {s.common[item.key]}
              </a>
            ))}
            <LanguageMenu locale={locale} path={path} hrefFor={switcherHref(path, locale)} />
          </nav>
        </div>
      </header>

      <main id="main">{children}</main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <div>
            <p className="site-footer__tagline">{s.footer.tagline}</p>
            <p className="site-footer__meta">{s.footer.rights}</p>
            <p className="site-footer__meta">
              {s.footer.madeBy(BRAND.legalEntity)} ·{' '}
              <a href={`mailto:${BRAND.contactSupport}`}>{BRAND.contactSupport}</a>
            </p>
          </div>
          <nav className="site-footer__links" aria-label={s.common.navLandmarkFooter}>
            <a href={href('/how-it-works')}>{s.common.navHowItWorks}</a>
            <a href={href('/guides')}>{s.common.navGuides}</a>
            <a href={href('/blog')}>{s.common.navBlog}</a>
            <a href={href('/faq')}>{s.common.navFaq}</a>
            <a href={href('/privacy')}>{s.common.navPrivacy}</a>
            <a href={href('/group-trip-budget')}>{s.footer.groupTrips}</a>
            <a href={href('/group-gift-budget')}>{s.footer.groupGifts}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
